import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, Paper
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ForestIcon from '@mui/icons-material/Forest';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';

// ─── Theme colours ────────────────────────────────────────────────────────────
const PURPLE = '#6a1b9a';
const GREEN  = '#1b4332';
const BROWN  = '#6d4c41';

const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${BROWN} 0%, #3e2723 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.2,
  px: 0.8,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const CELL_SX = { py: 0.8, px: 0.8, fontSize: '0.76rem' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtNum(v, d = 2) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toLocaleString('en-IN', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

function fmtDate(v) {
  if (!v) return '—';
  return String(v).substring(0, 10);
}

function isGrandTotal(row) {
  return String(row.FOREST ?? '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Detail Table (RS[0]) ─────────────────────────────────────────────────────
const DETAIL_COLS = [
  { label: '#',               key: '__idx',             align: 'center' },
  { label: 'LOG NO',          key: 'LOGNO_ACTUAL',      align: 'center' },
  { label: 'FOREST',          key: 'FOREST',            align: 'center' },
  { label: 'ESSENCE',         key: 'ESSENCE',           align: 'center' },
  { label: 'QUALITY',         key: 'QUALITY',           align: 'center' },
  { label: 'DIA',             key: 'BORD_ACTUAL_DIA',   align: 'center', fmt: v => fmtNum(v, 0) },
  { label: 'LENGTH (m)',      key: 'LENGTH',            align: 'center', fmt: v => fmtNum(v, 2) },
  { label: 'CBM BORD.',       key: 'CBM_BORDEREAU',     align: 'center', fmt: v => fmtNum(v, 3) },
  { label: 'CBM ACTUAL',      key: 'BORD_ACTUAL_CBM',   align: 'center', fmt: v => fmtNum(v, 3) },
  { label: 'STATUS',          key: 'CURRENT_STATUS',    align: 'center' },
  { label: 'INVOICE NO',      key: 'INVOICE_NO',        align: 'center' },
  { label: 'INVOICE DATE',    key: 'INVOICE_DATE',      align: 'center', fmt: fmtDate },
  { label: 'RATE',            key: 'RATE',              align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'FINAL RATE',      key: 'FINAL_RATE',        align: 'right',  fmt: v => fmtNum(v, 6) },
  { label: 'SUPP. VALUE',     key: 'SUPPL_VALUE',       align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'SUPP. RATE/CBM', key: 'SUPPL_RATE_PER_CBM', align: 'right',  fmt: v => fmtNum(v, 6) },
  { label: 'FINAL RATE 2',    key: 'FINAL_RATE2',       align: 'right',  fmt: v => fmtNum(v, 6) },
  { label: 'STOCK VALUE',     key: 'STOCK_VALUE',       align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'MATERIAL COST',   key: 'METERIALCOST',      align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'COST/CBM',        key: 'Cost_per_CBM',      align: 'right',  fmt: v => fmtNum(v, 6) },
  { label: 'RECEIVING DATE',  key: 'RECEIVING_DATE',    align: 'center', fmt: fmtDate },
  { label: 'COMPANY',         key: 'COMPANY',           align: 'center' },
];

function DetailTable({ rows }) {
  if (!rows?.length) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 540, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 2000 }}>
        <TableHead>
          <TableRow>
            {DETAIL_COLS.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#fdf8f0',
              '&:hover': { backgroundColor: '#fbe9e7' },
            }}>
              {DETAIL_COLS.map(col => {
                const val = col.key === '__idx'
                  ? i + 1
                  : col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                return (
                  <TableCell key={col.label} sx={{ ...CELL_SX, textAlign: col.align }}>
                    {val}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary By Forest+Essence (RS[1]) ────────────────────────────────────────
const SUMMARY_FOREST_COLS = [
  { label: 'FOREST',         key: 'FOREST',          align: 'center' },
  { label: 'ESSENCE',        key: 'ESSENCE',         align: 'center' },
  { label: 'LOGS',           key: 'LOG_COUNT',       align: 'center', fmt: v => fmtNum(v, 0) },
  { label: 'CBM BORD.',      key: 'CBM_BORDEREAU',   align: 'right',  fmt: v => fmtNum(v, 3) },
  { label: 'CBM CALC.',      key: 'CBM_CALCULATION', align: 'right',  fmt: v => fmtNum(v, 3) },
  { label: 'CBM ACTUAL',     key: 'BORD_ACTUAL_CBM', align: 'right',  fmt: v => fmtNum(v, 3) },
  { label: 'TOTAL COST',     key: 'TOTAL_COST',      align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'STOCK VALUE',    key: 'STOCKt_VALUE',    align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'MATERIAL COST',  key: 'METERIALCOST',    align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'SUPPL. VALUE',   key: 'SUPPL_VALUE',     align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'CSS',            key: 'CSS',             align: 'right',  fmt: v => fmtNum(v, 2) },
];

function SummaryForestTable({ rows }) {
  if (!rows?.length) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow>
            {SUMMARY_FOREST_COLS.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#d1fae5' : (i % 2 === 0 ? '#fff' : '#fdf8f0'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {SUMMARY_FOREST_COLS.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      textAlign: col.align,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#14532d' : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary By Forest+Essence+Quality (RS[2]) ───────────────────────────────
const SUMMARY_QUALITY_COLS = [
  { label: 'FOREST',         key: 'FOREST',          align: 'center' },
  { label: 'ESSENCE',        key: 'ESSENCE',         align: 'center' },
  { label: 'QUALITY',        key: 'QUALITY',         align: 'center' },
  { label: 'LOGS',           key: 'LOG_COUNT',       align: 'center', fmt: v => fmtNum(v, 0) },
  { label: 'CBM BORD.',      key: 'CBM_BORDEREAU',   align: 'right',  fmt: v => fmtNum(v, 3) },
  { label: 'CBM CALC.',      key: 'CBM_CALCULATION', align: 'right',  fmt: v => fmtNum(v, 3) },
  { label: 'CBM ACTUAL',     key: 'BORD_ACTUAL_CBM', align: 'right',  fmt: v => fmtNum(v, 3) },
  { label: 'TOTAL COST',     key: 'TOTAL_COST',      align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'STOCK VALUE',    key: 'STOCKt_VALUE',    align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'MATERIAL COST',  key: 'METERIALCOST',    align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'SUPPL. VALUE',   key: 'SUPPL_VALUE',     align: 'right',  fmt: v => fmtNum(v, 2) },
  { label: 'CSS',            key: 'CSS',             align: 'right',  fmt: v => fmtNum(v, 2) },
];

function SummaryQualityTable({ rows }) {
  if (!rows?.length) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            {SUMMARY_QUALITY_COLS.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#d1fae5' : (i % 2 === 0 ? '#fff' : '#fdf8f0'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {SUMMARY_QUALITY_COLS.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      textAlign: col.align,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#14532d' : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, color = BROWN }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2,
      border: `1.5px solid ${color}30`,
      background: `${color}08`,
      textAlign: 'center',
      minWidth: 120,
    }}>
      <Typography sx={{ fontSize: '0.67rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const TAB_LABELS = [
  'DETAIL',
  'SUMMARY — FOREST',
  'SUMMARY — FOREST + QUALITY',
];

const LogBordeauReportV2View = ({ onBackClick, user }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate,   setToDate]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [data,     setData]     = useState(null);
  const [tab,      setTab]      = useState(0);

  const fetchData = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both From and To dates');
      return;
    }
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await fetch(`${API_URL}/order/bordeaureportv2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('Server error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.success) throw new Error(apiData.message || 'API error');
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date range.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const fd = fromDate.format('YYYY-MM-DD');
    const td = toDate.format('YYYY-MM-DD');
    [
      { label: 'Detail',                     rows: data[0] },
      { label: 'Summary by Forest',          rows: data[1] },
      { label: 'Summary by Forest+Quality',  rows: data[2] },
    ].forEach(({ label, rows }) => {
      if (rows?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
      }
    });
    XLSX.writeFile(wb, `LogBordeauV2_${fd}_to_${td}.xlsx`);
  };

  // Grand total from RS[1] for stat chips
  const grandTotal = data?.[1]?.find(r => isGrandTotal(r));

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <Card sx={{
        mb: 3, borderRadius: 3,
        boxShadow: '0 4px 12px rgba(109,76,65,0.12)',
        border: '1px solid #efebe9',
      }}>
        <CardContent sx={{ p: 3 }}>

          {/* Title row */}
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: BROWN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{
              background: `linear-gradient(135deg, ${BROWN} 0%, #3e2723 100%)`,
              borderRadius: 2, p: 1.5, mr: 2,
            }}>
              <ForestIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: BROWN }}>
                Bordereau Datewise Summary
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                Log-wise cost, stock value, supplements &amp; summary — by date range
              </Typography>
            </Box>
          </Box>

          {/* Date pickers */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: BROWN,
                  '&:hover': { backgroundColor: '#4e342e' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {loading ? 'Loading…' : 'Search'}
              </Button>
            </Box>
          </LocalizationProvider>

          {/* Stat chips */}
          {grandTotal && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={2.5}>
              <StatChip
                label="Total Logs"
                value={fmtNum(grandTotal.LOG_COUNT, 0)}
                color={BROWN}
              />
              <StatChip
                label="CBM Bordereau"
                value={fmtNum(grandTotal.CBM_BORDEREAU, 3)}
                color={BROWN}
              />
              <StatChip
                label="CBM Actual"
                value={fmtNum(grandTotal.BORD_ACTUAL_CBM, 3)}
                color={GREEN}
              />
              <StatChip
                label="Stock Value"
                value={fmtNum(grandTotal.STOCKt_VALUE, 2)}
                color="#c62828"
              />
              <StatChip
                label="Material Cost"
                value={fmtNum(grandTotal.METERIALCOST, 2)}
                color="#e65100"
              />
              <StatChip
                label="Suppl. Value"
                value={fmtNum(grandTotal.SUPPL_VALUE, 2)}
                color={PURPLE}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Loading / Error ──────────────────────────────────────────────────── */}
      {loading && (
        <Box textAlign="center" py={6}>
          <CircularProgress sx={{ color: BROWN }} />
          <Typography sx={{ mt: 2, color: '#888' }}>Fetching data…</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Results card ─────────────────────────────────────────────────────── */}
      {data && (
        <Card sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #efebe9',
        }}>
          {/* Tab bar + download */}
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: BROWN },
                '& .MuiTabs-indicator': { backgroundColor: BROWN },
              }}
            >
              {TAB_LABELS.map((l, i) => (
                <Tab key={i} label={l} />
              ))}
            </Tabs>

            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ color: BROWN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}
            >
              Excel
            </Button>
          </Box>

          {/* Tab content */}
          {tab === 0 && <DetailTable          rows={data[0]} />}
          {tab === 1 && <SummaryForestTable   rows={data[1]} />}
          {tab === 2 && <SummaryQualityTable  rows={data[2]} />}
        </Card>
      )}
    </Container>
  );
};

export default LogBordeauReportV2View;
